import { useCallback, useState } from "react";
import moment from "moment";

// List endpoints paginate on the server when `page` is sent: the response then
// carries `total`, `page`, `limit` and `pages`. The backend caps `limit` at 100.
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// The backend caps `search` at 100 characters.
const SEARCH_MAX_LENGTH = 100;

// A new search or sort goes back to the first page in the same state update,
// so the table requests page 1 once rather than the old page and then page 1.
const RESET_PAGE_ACTIONS = ["setGlobalFilter", "toggleSortBy", "setSortBy", "resetSortBy"];
const resetPageOnQueryChange = (state, action) => (RESET_PAGE_ACTIONS.includes(action.type) ? { ...state, pageIndex: 0 } : state);

// Options to spread into useTable so it shows the server's page as-is, lets
// the server decide how many pages there are, and leaves search and sorting
// to the server. A response without `pages` (GET areas ignores `page`) is the
// whole searched and sorted result, so the table pages through it itself.
export const serverPaginationOptions = (response) => ({
  manualPagination: !response || response.pages !== undefined,
  pageCount: response?.pages ?? 0,
  autoResetPage: false,
  manualGlobalFilter: true,
  autoResetGlobalFilter: false,
  manualSortBy: true,
  autoResetSortBy: false,
  stateReducer: resetPageOnQueryChange,
});

// The API's `sort` for react-table's sortBy. A column sorts by its id unless
// it names the API field with `sortField`; columns the server can't sort set
// `disableSortBy`.
export const sortParam = (sortBy, columns, fallback) => {
  const fields = (sortBy || []).map(({ id, desc }) => {
    const column = columns.find((c) => (c.id || c.accessor) === id);
    return (desc ? "-" : "") + (column?.sortField || id);
  });
  return fields.length > 0 ? fields.join(",") : fallback;
};

// Query params for one page of a list: page, limit, sort and search.
export const listParams = ({ pageIndex, pageSize, globalFilter, sortBy }, columns, defaultSort) => {
  const search = globalFilter?.trim().slice(0, SEARCH_MAX_LENGTH);
  const sort = sortParam(sortBy, columns, defaultSort);
  return {
    page: pageIndex + 1,
    limit: pageSize,
    ...(sort ? { sort } : {}),
    ...(search ? { search } : {}),
  };
};

// Serial number of the first row on the current page, for the S/N column.
export const pageOffset = (response) => ((response?.page || 1) - 1) * (response?.limit || 0);

// Start of the current month as an ISO string, for `createdAt[gte]` filters.
export const startOfMonth = () => moment().startOf("month").toISOString();

// Table state reported by a DataTable through onPageChange. Starts as null
// until the table reports, and ignores repeats of the same state (the table
// reports again when it remounts after the first-load spinner).
export const useTablePage = () => {
  const [tableState, setTableState] = useState(null);
  const onPageChange = useCallback(({ pageIndex, pageSize, globalFilter, sortBy }) => {
    const next = { pageIndex, pageSize, globalFilter, sortBy };
    setTableState((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, []);
  return [tableState, onPageChange];
};

// Number of documents matching `params`, from a one-row page. Uses the cached
// (non-lazy) query hook, so it refetches whenever the list's tags are
// invalidated.
export const useCount = (useQuery, params, options) => {
  const { data } = useQuery({ ...params, page: 1, limit: 1, populate: "false" }, options);
  return data?.total ?? 0;
};
